import { Injectable } from '@angular/core';
import { EnvironmentService } from '../environment.service';
import { ClarityProjectKey } from './clarity-project-keys.enum';
import { ClarityUtils } from './clarity.utils';

@Injectable({
  providedIn: 'root',
})
export class SgClarityService {
  private _isInitialized = false;
  private _currentProjectId: string | null = null;
  private _currentProjectKey: ClarityProjectKey | null = null;

  constructor(private envService: EnvironmentService) {}

  /**
   * Initialize Microsoft Clarity with the provided project key
   * @param projectKey - The Clarity project key from ClarityProjectKey enum
   * @returns Promise that resolves with boolean indicating if initialization actually occurred
   */
  async initialize(projectKey: ClarityProjectKey): Promise<boolean> {
    if (!this.isProductionEnvironment()) {
      console.warn(`[SgClarityService] Clarity initialization skipped in '${this.envService.env}' environment`);
      return Promise.resolve(false);
    }

    const projectId = ClarityUtils.getProjectIdForKey(projectKey);
    if (!projectId) {
      const message = `Project ID not configured for '${projectKey}'. Please set CLARITY_PROJECT_IDS[${projectKey}].`;
      console.warn(`[SgClarityService] ${message}`);
      return Promise.reject(new Error(message));
    }

    if (this._isInitialized && this._currentProjectKey === projectKey) {
      console.warn(`[SgClarityService] Clarity already initialized for '${projectKey}'`);
      return Promise.resolve(true);
    }

    if (this._isInitialized && this._currentProjectKey !== projectKey) {
      console.warn(
        `[SgClarityService] Clarity already initialized for '${this._currentProjectKey}'. Skipping re-initialization for '${projectKey}'.`
      );
      return Promise.resolve(true);
    }

    try {
      await ClarityUtils.initializeClarity(projectId);
      this._isInitialized = true;
      this._currentProjectId = projectId;
      this._currentProjectKey = projectKey;
      console.log(`[SgClarityService] Microsoft Clarity initialized successfully for '${projectKey}'`);
      return true;
    } catch (error) {
      console.error('[SgClarityService] Failed to initialize Microsoft Clarity:', error);
      throw error;
    }
  }

  /**
   * Check if Clarity is initialized
   * @returns boolean indicating initialization status
   */
  isReady(): boolean {
    return this._isInitialized;
  }

  /**
   * Get the current project ID
   * @returns Current project ID or null if not initialized
   */
  getProjectId(): string | null {
    return this._currentProjectId;
  }

  /**
   * Get the current project key
   * @returns Current project key or null if not initialized
   */
  getProjectKey(): ClarityProjectKey | null {
    return this._currentProjectKey;
  }

  /**
   * Identify a user (enhanced with all official parameters)
   * @param customId - The unique identifier for the customer (required)
   * @param customSessionId - A custom session identifier (optional)
   * @param customPageId - A custom page identifier (optional)
   * @param friendlyName - A friendly name for the customer (optional)
   * @returns boolean indicating if the operation was executed
   */
  identify(customId: string, customSessionId?: string, customPageId?: string, friendlyName?: string): boolean {
    if (!this.isProductionEnvironment()) {
      return false;
    }
    if (!this._isInitialized) {
      console.warn('[SgClarityService] Clarity not initialized. Call initialize() first.');
      return false;
    }
    ClarityUtils.identifyUser(customId, customSessionId, customPageId, friendlyName);
    return true;
  }

  /**
   * Set custom tags (enhanced to support string arrays)
   * @param key - Tag key
   * @param value - Tag value (can be string or string array)
   * @returns boolean indicating if the operation was executed
   */
  setTag(key: string, value: string | string[]): boolean {
    if (!this.isProductionEnvironment()) {
      return false;
    }
    if (!this._isInitialized) {
      console.warn('[SgClarityService] Clarity not initialized. Call initialize() first.');
      return false;
    }
    ClarityUtils.setTag(key, value);
    return true;
  }

  /**
   * Track a custom event
   * @param eventName - The name of the event
   * @returns boolean indicating if the operation was executed
   */
  trackEvent(eventName: string): boolean {
    if (!this.isProductionEnvironment()) {
      return false;
    }
    if (!this._isInitialized) {
      console.warn('[SgClarityService] Clarity not initialized. Call initialize() first.');
      return false;
    }
    ClarityUtils.trackEvent(eventName);
    return true;
  }

  /**
   * Set cookie consent for GDPR compliance
   * @param consent - Whether consent is given (default: true)
   * @returns boolean indicating if the operation was executed
   */
  setCookieConsent(consent: boolean = true): boolean {
    if (!this.isProductionEnvironment()) {
      return false;
    }
    if (!this._isInitialized) {
      console.warn('[SgClarityService] Clarity not initialized. Call initialize() first.');
      return false;
    }
    ClarityUtils.setCookieConsent(consent);
    return true;
  }

  /**
   * Upgrade session for priority recording
   * @param reason - The reason for the upgrade
   * @returns boolean indicating if the operation was executed
   */
  upgradeSession(reason: string): boolean {
    if (!this.isProductionEnvironment()) {
      return false;
    }
    if (!this._isInitialized) {
      console.warn('[SgClarityService] Clarity not initialized. Call initialize() first.');
      return false;
    }
    ClarityUtils.upgradeSession(reason);
    return true;
  }

  /**
   * Check if the service should execute (only in production)
   */
  private isProductionEnvironment(): boolean {
    return this.envService.env === 'prod';
  }
}
